import xlsx, { ISettings, IJsonSheet, IColumn, IContent } from "json-as-xlsx";
import moment from "moment";
import {
  RelatorioReceitasPdfResponse,
  ContasRelatorioReceitas,
  ResumoCategoriaItem,
  RelatorioReceitasFields,
  RelatorioReceitasItem,
} from "core/features/relatorios/typings";
import { formatMoney } from "infra/helpers/number";
import { defaultContentMapperXlsx } from "infra/packages/json-as-xlsx/index";
import { sanitizeSheetName } from "infra/helpers/sanitizeSheetName";

type TotaisSheetItem = Pick<
  RelatorioReceitasPdfResponse,
  "totalCobrancas" | "totalUnidades" | "valorTotal" | "totalPessoas"
>;

export function exportExcelRelatorioReceitas({
  data,
  selectedFields = [],
}: {
  data: RelatorioReceitasPdfResponse;
  selectedFields?: RelatorioReceitasFields[];
}) {
  const settings: ISettings = {
    fileName: `Relatório de Receitas - ${moment().format("DD_MM_YYYY")}`,
    extraLength: 3,
    writeOptions: {},
  };

  const columnsUnidades = getColumnsUnidades(!!data?.totalUnidades);
  const columnsTotais = getColumnsTotais(!!data?.totalUnidades);

  const columns = getColumns(selectedFields);

  const sheets: IJsonSheet[] = [];

  sheets.push({
    sheet: `Receitas (${formatMoney(data.valorTotal)})`.slice(0, 31),
    columns: columnsUnidades,
    content: data.items.map(defaultContentMapperXlsx),
  });

  sheets.push({
    sheet: `Totais`,
    columns: columnsTotais,
    content: [
      {
        totalCobrancas: data.totalCobrancas,
        totalUnidades: data.totalUnidades,
        totalPessoas: data.totalPessoas,
        valorTotal: data.valorTotal,
      },
    ],
  });

  if (selectedFields.includes("resumoContaCategoria")) {
    const columnsResumo = getColumnsResumo();

    sheets.push({
      sheet: "Resumo por categoria",
      columns: columnsResumo,
      content: data.resumoPorCategoria.map(defaultContentMapperXlsx),
    });
  }

  data.items.forEach((item) => {
    const sheetName = `${
      selectedFields.includes("unidade") ? item.unidade : ""
    }${
      selectedFields.includes("unidade") &&
      selectedFields.includes("nomeCondomino") &&
      item.unidadeId
        ? " - "
        : ""
    }${selectedFields.includes("nomeCondomino") ? item.proprietario : ""}`;
    sheets.push({
      sheet: sanitizeSheetName(sheetName.slice(0, 31)),
      columns: columns,
      content: item.contas.map(defaultContentMapperXlsx),
    });
  });

  xlsx(sheets, settings);
}

function getColumnsUnidades(includeUnidade = true) {
  const columns: IColumn[] = [];

  if (includeUnidade) {
    columns.push({
      label: "Unidade",
      value: (row: IContent) =>
        (row as unknown as RelatorioReceitasItem).unidade,
    });
  }

  columns.push({
    label: includeUnidade ? "Proprietário" : "Pessoa",
    value: (row: IContent) =>
      (row as unknown as RelatorioReceitasItem).proprietario,
  });

  columns.push({
    label: "Valor",
    format: "#,###0.00",
    value: (row: IContent) =>
      (row as unknown as RelatorioReceitasItem).valorTotal,
  });

  return columns;
}

function getColumns(selectedFields: RelatorioReceitasFields[] = []) {
  const columns: IColumn[] = [];

  if (selectedFields.includes("descricao")) {
    columns.push({
      label: "Descrição",
      value: (row: IContent) =>
        (row as unknown as ContasRelatorioReceitas).descricao!,
    });
  }

  if (selectedFields.includes("geracao")) {
    columns.push({
      label: "Geração",
      format: "dd/mm/yyyy",
      value: (row: IContent) =>
        moment((row as unknown as ContasRelatorioReceitas).createdAt).format(
          "DD/MM/YYYY"
        ),
    });
  }

  if (selectedFields.includes("vencimento")) {
    columns.push({
      label: "Vencimento",
      format: "dd/mm/yyyy",
      value: (row: IContent) =>
        moment(
          (row as unknown as ContasRelatorioReceitas).dataVencimento
        ).format("DD/MM/YYYY"),
    });
  }

  if (selectedFields.includes("liquidacao")) {
    columns.push({
      label: "Liquidação",
      format: "dd/mm/yyyy",
      value: (row: IContent) => {
        const date = (row as unknown as ContasRelatorioReceitas).dataPagamento;
        return date ? moment(date).format("DD/MM/YYYY") : "--";
      },
    });
  }

  if (selectedFields.includes("credito")) {
    columns.push({
      label: "Crédito",
      format: "dd/mm/yyyy",
      value: (row: IContent) => {
        const date = (row as unknown as ContasRelatorioReceitas)
          .dataMovimentacao;
        return date ? moment(date).format("DD/MM/YYYY") : "--";
      },
    });
  }

  if (selectedFields.includes("centroDeCusto")) {
    columns.push({
      label: "Crédito",
      value: (row: IContent) =>
        (row as unknown as ContasRelatorioReceitas).centroDeCusto?.nome ?? "--",
    });
  }

  if (selectedFields.includes("nossoNumero")) {
    columns.push({
      label: "Nosso Número",
      value: (row: IContent) =>
        (row as unknown as ContasRelatorioReceitas).nossoNumero ?? "",
    });
  }

  if (selectedFields.includes("valor")) {
    columns.push({
      label: "Total",
      format: "#,###0.00",
      value: (row: IContent) =>
        (row as unknown as ContasRelatorioReceitas).valor,
    });
  }

  return columns;
}

function getColumnsTotais(includeUnidade = true) {
  const columns: IColumn[] = [];

  columns.push({
    label: "Número de cobranças",
    format: "#,###0",
    value: (row: IContent) =>
      (row as unknown as TotaisSheetItem).totalCobrancas,
  });

  if (includeUnidade) {
    columns.push({
      label: "Número de unidades",
      format: "#,###0",
      value: (row: IContent) =>
        (row as unknown as TotaisSheetItem).totalUnidades,
    });
  } else {
    columns.push({
      label: "Número de pessoas",
      format: "#,###0",
      value: (row: IContent) =>
        (row as unknown as TotaisSheetItem).totalPessoas,
    });
  }

  columns.push({
    label: "Valor total",
    format: "#,###0.00",
    value: (row: IContent) => (row as unknown as TotaisSheetItem).valorTotal,
  });

  return columns;
}

function getColumnsResumo() {
  const columns: IColumn[] = [];

  columns.push({
    label: "Categoria",
    value: (row: IContent) => (row as unknown as ResumoCategoriaItem).nome,
  });

  columns.push({
    label: "Total",
    format: "#,###0.00",
    value: (row: IContent) => (row as unknown as ResumoCategoriaItem).valor,
  });

  return columns;
}
